// src/layouts/CustomMenu.js
import * as React from "react";
import {
  MenuItemLink,
  usePermissions,
  useSidebarState,
} from "react-admin";

import { List, ListSubheader, Divider } from "@mui/material";

// 🌈 COLORFUL ICONS (Color applied via sx)
import DashboardIcon from "@mui/icons-material/Dashboard";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CalculateIcon from "@mui/icons-material/Calculate";
import PaymentIcon from "@mui/icons-material/Payments";
import TableChartIcon from "@mui/icons-material/TableChart";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import WalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import UploadIcon from "@mui/icons-material/CloudUpload";
import DocumentIcon from "@mui/icons-material/Description";
import CreateIcon from "@mui/icons-material/Create";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";


// -----------------------------------------
// 🌈 COLOR PALETTE FOR ICONS
// -----------------------------------------
const iconColor = {
  dashboard: "#4f46e5",
  company: "#0ea5e9",
  people: "#3b82f6",
  car: "#10b981",
  tree: "#8b5cf6",
  calc: "#f97316",
  payment: "#e11d48",
  table: "#1d4ed8",
  assign: "#6366f1",
  receipt: "#7c3aed",
  wallet: "#059669",
  price: "#db2777",
  parking: "#f59e0b",
  upload: "#2563eb",
  document: "#6b7280",

createLetter: "#3b82f6",      // Blue
pendingLetter: "#f59e0b",     // Amber
signedLetter: "#10b981",      // Green
uploadLetterhead: "#ec4899",  // Pink
mdSignature: "#8b5cf6",       // Purple
  transactionEntry: "#14b8a6",  // Teal for Transaction Entry
  installmentAlert: "#dc2626",

};


// -----------------------------------------
// 🌈 BEAUTIFIED SECTION TITLES
// -----------------------------------------
function Section({ title }) {
  return (
    <ListSubheader
      sx={{
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: 0.8,
        color: "#374151",
        textTransform: "uppercase",
        bgcolor: "transparent",
        pl: 2,
        mt: 2,
        mb: 0.5,
        borderLeft: "4px solid #4f46e5",
      }}
    >
      {title}
    </ListSubheader>
  );
}

// -----------------------------------------
// 🌈 BEAUTIFIED LINK WRAPPER
// -----------------------------------------
function ColorItem({ icon, ...props }) {
  return (
    <MenuItemLink
      {...props}
      leftIcon={icon}
      sx={{
        "& .RaMenuItemLink-icon": { mr: 1 },
        borderRadius: "6px",
        mx: 1,
        my: 0.3,
        py: 0.7,
        px: 1,
        transition: "0.15s",
        "&:hover": {
          backgroundColor: "rgba(79,70,229,0.08)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
        },
      }}
    />
  );
}

//
// ========================================================
//  ADMIN MENU
// ========================================================
//
function AdminMenu(props) {
  const [sidebarOpen] = useSidebarState();
  const commonProps = { onClick: props.onMenuClick, sidebarIsOpen: sidebarOpen };

  return (
    <List component="nav">

      <ColorItem
        to="/"
        primaryText="Dashboard"
        icon={<DashboardIcon sx={{ color: iconColor.dashboard }} />}
        {...commonProps}
      />

      <Divider sx={{ my: 1 }} />

      <Section title="Company Management" />

      <ColorItem to="/companies" primaryText="Companies" icon={<BusinessIcon sx={{ color: iconColor.company }} />} {...commonProps} />
      <ColorItem to="/drivers" primaryText="Employees" icon={<PeopleIcon sx={{ color: iconColor.people }} />} {...commonProps} />
      <ColorItem to="/employee-docs-approvals" primaryText="Employee Docs Approvals" icon={<UploadFileIcon sx={{ color: iconColor.docs }} />} {...commonProps} />
      <ColorItem to="/vehicles" primaryText="Vehicles" icon={<DirectionsCarIcon sx={{ color: iconColor.car }} />} {...commonProps} />
      <ColorItem to="/driverPlanManager" primaryText="Driver Plan Manager" icon={<CalculateIcon sx={{ color: iconColor.calc }} />} {...commonProps} />
      <ColorItem to="/driverAssignmentCostSetup" primaryText="Assignment Cost Setup" icon={<CalculateIcon sx={{ color: iconColor.calc }} />} {...commonProps} />
      <ColorItem to="/installmentAdjustments" primaryText="Installment Adjustments" icon={<PaymentIcon sx={{ color: iconColor.payment }} />} {...commonProps} />
      <ColorItem to="/installmentCompletionAlerts" primaryText="Installment Completion Alerts" icon={<NotificationImportantIcon sx={{ color: iconColor.installmentAlert }} />} {...commonProps} />
      <ColorItem to="/driverMappings" primaryText="Driver Mappings" icon={<AccountTreeIcon sx={{ color: iconColor.tree }} />} {...commonProps} />
      <ColorItem to="/posDeviceMappings" primaryText="POS Device Mappings" icon={<PaymentIcon sx={{ color: iconColor.payment }} />} {...commonProps} />

      <Section title="Reports" />
<ColorItem to="/ledger" primaryText="Ledger" icon={<TableChartIcon sx={{ color: iconColor.table }} />} {...commonProps} />
<ColorItem to="/driversStatement" primaryText="Drivers Statement" icon={<AssignmentIcon sx={{ color: iconColor.assign }} />} {...commonProps} />
<ColorItem to="/carSheetCompanyPLExtract" primaryText="Car Sheet P&L Extract" icon={<TableChartIcon sx={{ color: iconColor.table }} />} {...commonProps} />
<ColorItem to="/car-idle-report" primaryText="Car Idle Report" icon={<HourglassEmptyIcon sx={{ color: iconColor.pendingLetter }} />} {...commonProps} />
<ColorItem to="/mdDailyIncomeReport" primaryText="Daily Income Report" icon={<TableChartIcon sx={{ color: iconColor.table }} />} {...commonProps} />

      <Section title="Finance" />
      <ColorItem to="/accounts" primaryText="Accounts" icon={<ReceiptIcon sx={{ color: iconColor.receipt }} />} {...commonProps} />
      <ColorItem to="/transactions" primaryText="Transactions" icon={<PaymentIcon sx={{ color: iconColor.payment }} />} {...commonProps} />
      <ColorItem to="/transactions-entry" primaryText="Transaction Entry" icon={<CreateIcon sx={{ color: iconColor.transactionEntry }} />} {...commonProps} />
      <ColorItem to="/upcomingExpenses" primaryText="Upcoming Expenses" icon={<WalletIcon sx={{ color: iconColor.wallet }} />} {...commonProps} />
      <ColorItem to="/invoiceGenerator" primaryText="Invoice Generator" icon={<DocumentIcon sx={{ color: iconColor.document }} />} {...commonProps} />

      <Section title="Trips" />
      <ColorItem to="/trips_pricing" primaryText="Pricing Update" icon={<PriceCheckIcon sx={{ color: iconColor.price }} />} {...commonProps} />
      <ColorItem to="/trips_parking" primaryText="Parking Update" icon={<LocalParkingIcon sx={{ color: iconColor.parking }} />} {...commonProps} />
      <ColorItem to="/client_pricing_setup" primaryText="Client Pricing Setup" icon={<PriceCheckIcon sx={{ color: iconColor.price }} />} {...commonProps} />

      <Section title="Import Income & Expenses" />
      <ColorItem to="/earningsImport" primaryText="Earnings Import" icon={<UploadIcon sx={{ color: iconColor.upload }} />} {...commonProps} />
      <ColorItem to="/dailyRideHailingUpload" primaryText="Daily Uber / Bolt Upload" icon={<UploadIcon sx={{ color: iconColor.upload }} />} {...commonProps} />
      <ColorItem to="/dailyBookingPortalUpload" primaryText="Daily Booking Portal Income" icon={<UploadIcon sx={{ color: iconColor.upload }} />} {...commonProps} />
      <ColorItem to="/rideHailingUploadHistory" primaryText="Ride-Hailing Upload History" icon={<UploadIcon sx={{ color: iconColor.upload }} />} {...commonProps} />
      <ColorItem to="/rideHailingRangeReport" primaryText="Ride-Hailing Date Range Report" icon={<TableChartIcon sx={{ color: iconColor.table }} />} {...commonProps} />
      <ColorItem to="/rideHailingWeekPeriods" primaryText="Ride-Hailing Week Periods" icon={<UploadIcon sx={{ color: iconColor.upload }} />} {...commonProps} />
      <ColorItem to="/posUploadHistory" primaryText="POS Upload History" icon={<UploadIcon sx={{ color: iconColor.upload }} />} {...commonProps} />
      <ColorItem to="/runningExpensesUpload" primaryText="Running Expenses Upload" icon={<UploadIcon sx={{ color: iconColor.upload }} />} {...commonProps} />

      <Section title="Letters" />

<ColorItem
  to="/letters"
  primaryText="Create Letter"
  icon={<CreateIcon sx={{ color: iconColor.createLetter }} />}
  {...commonProps}
/>

<ColorItem
  to="/pendingLetters"
  primaryText="Pending Letters"
  icon={<HourglassEmptyIcon sx={{ color: iconColor.pendingLetter }} />}
  {...commonProps}
/>

<ColorItem
  to="/signedLetters"
  primaryText="Signed Letters"
  icon={<CheckCircleIcon sx={{ color: iconColor.signedLetter }} />}
  {...commonProps}
/>

<ColorItem
  to="/letterHeadUpload"
  primaryText="Letterhead Upload"
  icon={<UploadFileIcon sx={{ color: iconColor.uploadLetterhead }} />}
  {...commonProps}
/>


      {props.logout}
    </List>
  );
}

//
// ========================================================
// MD MENU
// ========================================================
//
function MDMenu(props) {
  const [sidebarOpen] = useSidebarState();
  const commonProps = { onClick: props.onMenuClick, sidebarIsOpen: sidebarOpen };

  return (
    <List component="nav">
      <ColorItem
        to="/"
        primaryText="Dashboard"
        icon={<DashboardIcon sx={{ color: iconColor.dashboard }} />}
        {...commonProps}
      />

      <Divider sx={{ my: 1 }} />

      <Section title="Company Management" />
      <ColorItem to="/companies" primaryText="Companies" icon={<BusinessIcon sx={{ color: iconColor.company }} />} {...commonProps} />
      <ColorItem to="/drivers" primaryText="Employees" icon={<PeopleIcon sx={{ color: iconColor.people }} />} {...commonProps} />
      <ColorItem to="/vehicles" primaryText="Vehicles" icon={<DirectionsCarIcon sx={{ color: iconColor.car }} />} {...commonProps} />
      <ColorItem to="/driverPlanManager" primaryText="Driver Plan Manager" icon={<CalculateIcon sx={{ color: iconColor.calc }} />} {...commonProps} />
      <ColorItem to="/driverAssignmentCostSetup" primaryText="Assignment Cost Setup" icon={<CalculateIcon sx={{ color: iconColor.calc }} />} {...commonProps} />
      <ColorItem to="/installmentAdjustments" primaryText="Installment Adjustments" icon={<PaymentIcon sx={{ color: iconColor.payment }} />} {...commonProps} />
      <ColorItem to="/installmentCompletionAlerts" primaryText="Installment Completion Alerts" icon={<NotificationImportantIcon sx={{ color: iconColor.installmentAlert }} />} {...commonProps} />
      <ColorItem to="/driverMappings" primaryText="Driver Mappings" icon={<AccountTreeIcon sx={{ color: iconColor.tree }} />} {...commonProps} />
      <ColorItem to="/posDeviceMappings" primaryText="POS Device Mappings" icon={<PaymentIcon sx={{ color: iconColor.payment }} />} {...commonProps} />

      <Section title="Reports" />
      <ColorItem to="/ledger" primaryText="Ledger" icon={<TableChartIcon sx={{ color: iconColor.table }} />} {...commonProps} />
      <ColorItem to="/driversStatement" primaryText="Drivers Statement" icon={<AssignmentIcon sx={{ color: iconColor.assign }} />} {...commonProps} />
<ColorItem to="/carSheetCompanyPLExtract" primaryText="Car Sheet P&L Extract" icon={<TableChartIcon sx={{ color: iconColor.table }} />} {...commonProps} />
      <ColorItem to="/mdDailyIncomeReport" primaryText="Daily Income Report" icon={<TableChartIcon sx={{ color: iconColor.table }} />} {...commonProps} />
      <ColorItem to="/upcomingExpenses" primaryText="Upcoming Expenses" icon={<UploadIcon sx={{ color: iconColor.upload }} />} {...commonProps} />
      <ColorItem to="/car-idle-report" primaryText="Car Idle Report" icon={<HourglassEmptyIcon sx={{ color: iconColor.pendingLetter }} />} {...commonProps} />

      <Section title="Letters" />

<ColorItem
  to="/letters"
  primaryText="Create Letters"
  icon={<CreateIcon sx={{ color: iconColor.createLetter }} />}
  {...commonProps}
/>

<ColorItem
  to="/pendingLetters"
  primaryText="Pending Letters"
  icon={<HourglassEmptyIcon sx={{ color: iconColor.pendingLetter }} />}
  {...commonProps}
/>

<ColorItem
  to="/signedLetters"
  primaryText="Signed Letters"
  icon={<CheckCircleIcon sx={{ color: iconColor.signedLetter }} />}
  {...commonProps}
/>

<ColorItem
  to="/mdSignature"
  primaryText="Signature & Stamp Upload"
  icon={<BorderColorIcon sx={{ color: iconColor.mdSignature }} />}
  {...commonProps}
/>


      {props.logout}
    </List>
  );
}

//
// ========================================================
// OPERATION MENU
// ========================================================
//
function OperationMenu(props) {
  const [sidebarOpen] = useSidebarState();
  const commonProps = { onClick: props.onMenuClick, sidebarIsOpen: sidebarOpen };

  return (
    <List component="nav">
      <ColorItem
        to="/"
        primaryText="Dashboard"
        icon={<DashboardIcon sx={{ color: iconColor.dashboard }} />}
        {...commonProps}
      />

      <Divider sx={{ my: 1 }} />

      <Section title="Employees" />
      <ColorItem
        to="/drivers"
        primaryText="View Employees & Documents"
        icon={<PeopleIcon sx={{ color: iconColor.people }} />}
        {...commonProps}
      />

      <Section title="Reports" />
      <ColorItem
        to="/driversStatement"
        primaryText="Drivers Statement"
        icon={<AssignmentIcon sx={{ color: iconColor.assign }} />}
        {...commonProps}
      />
      <ColorItem
        to="/mdDailyIncomeReport"
        primaryText="Daily Income Report"
        icon={<TableChartIcon sx={{ color: iconColor.table }} />}
        {...commonProps}
      />

      {props.logout}
    </List>
  );
}

//
// ========================================================
// DOCUMENT LINK USER MENU
// ========================================================
//
function DocsLinkMenu(props) {
  const [sidebarOpen] = useSidebarState();
  const commonProps = { onClick: props.onMenuClick, sidebarIsOpen: sidebarOpen };

  return (
    <List component="nav">
      <ColorItem
        to="/"
        primaryText="Dashboard"
        icon={<DashboardIcon sx={{ color: iconColor.dashboard }} />}
        {...commonProps}
      />

      <Divider sx={{ my: 1 }} />

      <Section title="Employee Documents" />
      <ColorItem
        to="/drivers"
        primaryText="Driver Documents"
        icon={<UploadFileIcon sx={{ color: iconColor.docs }} />}
        {...commonProps}
      />
      {props.logout}
    </List>
  );
}

//
// ========================================================
// AUTO-ROLE SWITCH
// ========================================================
export default function CustomMenu(props) {
  const { permissions } = usePermissions();
  const role = (permissions || "").toLowerCase();

  if (role === "md") return <MDMenu {...props} />;
  if (role === "operation") return <OperationMenu {...props} />;
  if (role === "docs_link") return <DocsLinkMenu {...props} />;
  return <AdminMenu {...props} />;
}
